import { useState } from "react";
import { Search, Monitor } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GlassCard } from "@/components/ui/glass-card";
import { AppleDeviceIcon } from "@/components/icons";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";
import { normalizeStatus } from "@/hooks/useDevices";

interface TagDevicesTabProps { tagId: string; tagName?: string; }

function inferOsType(device: any): string {
  const platform = (device.platform || '').toLowerCase();
  if (platform) return platform;
  const product = (device.product || device.modelName || device.name || '').toLowerCase();
  if (product.includes('iphone')) return 'ios';
  if (product.includes('ipad')) return 'ipados';
  if (product.includes('mac')) return 'macos';
  if (product.includes('apple tv') || product.includes('appletv')) return 'tvos';
  return 'macos';
}

function statusVariant(status: string): 'default' | 'destructive' | 'secondary' {
  if (status === 'active') return 'default';
  if (status === 'inactive') return 'destructive';
  return 'secondary';
}

export const TagDevicesTab = ({ tagId, tagName }: TagDevicesTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ["tag-devices", tagId],
    queryFn: () => tagsApi.getDevicesByTag(tagId),
  });

  const uniqueDevices = devices.filter((device: any, index: number, allDevices: any[]) => {
    const identity = [
      device?.uuid,
      device?.id,
      device?.deviceId,
      device?.udid,
      device?.serialNumber,
      device?.name,
      device?.deviceName,
    ]
      .find((value) => value !== undefined && value !== null && String(value).trim() !== "");

    if (!identity) return index === 0;

    return allDevices.findIndex((candidate: any) => {
      const candidateIdentity = [
        candidate?.uuid,
        candidate?.id,
        candidate?.deviceId,
        candidate?.udid,
        candidate?.serialNumber,
        candidate?.name,
        candidate?.deviceName,
      ]
        .find((value) => value !== undefined && value !== null && String(value).trim() !== "");

      return String(candidateIdentity) === String(identity);
    }) === index;
  });

  const filteredDevices = uniqueDevices.filter((d: any) =>
    (d.name || d.deviceName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (d.hostname || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" hover={false} className="p-4 animate-pulse">
            <div className="flex items-center space-x-3 mb-3"><div className="w-8 h-8 rounded-lg bg-muted"></div><div className="flex-1 space-y-1"><div className="h-4 bg-muted rounded w-3/4"></div><div className="h-3 bg-muted rounded w-1/2"></div></div></div>
          </GlassCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search devices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9" />
        </div>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Monitor className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Devices Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {searchQuery ? "No devices match your search criteria." : "This tag has no devices assigned to it yet."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDevices.map((device: any, index: number) => {
            const osType = inferOsType(device);
            const status = normalizeStatus(device.status);
            const deviceName = device.name || device.deviceName || 'Unknown Device';
            const deviceId = device.uuid || device.id || device.deviceId || device.udid || `${tagId}-${index}`;

            return (
              <GlassCard key={deviceId} variant="subtle" className="p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <AppleDeviceIcon osType={osType} modelName={device.product || device.modelName || deviceName} className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/devices/${deviceId}?from=tag&tagId=${tagId}&tagName=${encodeURIComponent(tagName || 'Tag')}`}
                      className="font-medium text-sm hover:text-primary transition-colors block truncate">
                      {deviceName}
                    </Link>
                    {device.hostname && <p className="text-xs text-muted-foreground truncate">{device.hostname}</p>}
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Platform</span>
                    <span className="font-medium capitalize">{osType}</span>
                  </div>
                  {device.osVersion && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">OS Version</span>
                      <span className="font-medium">{device.osVersion}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant={statusVariant(status)} className="text-xs h-5 capitalize">
                      {status}
                    </Badge>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
};
